/**
 * Unified Idea Generator View
 *
 * Single UI for generating episode ideas with flexible parameters.
 * Features:
 * - Multi-select tags grouped by learning goal
 * - Multi-select skill levels
 * - Duration filter
 * - Ideas per combination and candidates per idea settings
 * - Fast mode (candidates=1) and Quality mode (candidates>1 with LLM selection)
 * - Grid results view showing selected ideas
 * - CSV export with all candidates
 *
 * Story: 11.16 - Unified Episode Idea Generator
 */

import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { EpisodeNavigation } from '../components/EpisodeNavigation';
import { useInitialData } from 'contexts/InitialDataContext';
import {
  useGenerateIdeas,
  generateCSV,
  downloadCSV,
  type UnifiedIdeaCandidate,
  type UnifiedIdeaGenerationRequest,
} from '../services/ideasApi';
import { getTagEnglishName } from 'services/initialDataApi';
import type { ParsedIdea } from '../hooks/useBulkEpisodeOrchestrator';

// ============================================================================
// Constants
// ============================================================================

const DURATION_OPTIONS = [
  { value: 'Micro', label: 'Micro' },
  { value: 'Short', label: 'Short' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Long', label: 'Long' },
  { value: 'Extended', label: 'Extended' },
];

// ============================================================================
// Component
// ============================================================================

export function IdeaGeneratorView() {
  const { state: initialDataState } = useInitialData();
  const navigate = useNavigate();

  // Form state - Story 15.8: Tag IDs are now integers
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [selectedLearningGoalIds, setSelectedLearningGoalIds] = useState<number[]>([]);
  const [selectedSkillLevels, setSelectedSkillLevels] = useState<number[]>([]);
  const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
  const [ideasPerCombination, setIdeasPerCombination] = useState(1);
  const [candidatesPerIdea, setCandidatesPerIdea] = useState(5);
  const [seedText, setSeedText] = useState('');
  const [modifiers, setModifiers] = useState({
    addCreativity: false,
    addCulturalDiversity: false,
    multiPartyConversations: false,
    cultureSpecific: false,
  });

  // Results state
  const [results, setResults] = useState<UnifiedIdeaCandidate[]>([]);

  // Generate mutation
  const generateMutation = useGenerateIdeas();

  // Get data from initial data
  const skillLevels = initialDataState.data?.skillLevels ?? [];

  // Group tags by learning goals
  const tagsByLearningGoal = useMemo(() => {
    const tags = initialDataState.data?.tags ?? [];
    const learningGoals = initialDataState.data?.learningGoals ?? [];

    if (!tags.length || !learningGoals.length) return {};

    const grouped: Record<
      number,
      { id: number; name: string; icon_emoji?: string; tags: typeof tags }
    > = {};

    learningGoals.forEach((goal) => {
      grouped[goal.id] = {
        id: goal.id,
        name: goal.name,
        icon_emoji: goal.icon_emoji,
        tags: [],
      };
    });

    tags.forEach((tag) => {
      const goalIds = tag.learning_goal_ids || [];
      goalIds.forEach((goalId: number) => {
        if (grouped[goalId]) {
          grouped[goalId].tags.push(tag);
        }
      });
    });

    // Remove empty categories
    Object.keys(grouped).forEach((key) => {
      const numKey = Number(key);
      if (grouped[numKey].tags.length === 0) {
        delete grouped[numKey];
      }
    });

    return grouped;
  }, [initialDataState.data?.tags, initialDataState.data?.learningGoals]);

  // ============================================================================
  // Computed values
  // ============================================================================

  const isLoading = generateMutation.isPending;
  // If durations selected, they multiply combinations; otherwise treat as 1
  const durationMultiplier = selectedDurations.length > 0 ? selectedDurations.length : 1;
  const inputCount = selectedTagIds.length + selectedLearningGoalIds.length;
  const totalCombinations = inputCount * selectedSkillLevels.length * durationMultiplier;
  const totalFinalIdeas = totalCombinations * ideasPerCombination;
  const totalCandidates = totalFinalIdeas * candidatesPerIdea;
  const isQualityMode = candidatesPerIdea > 1;

  // Estimate time: ~30s fast mode, ~60s quality mode per idea
  const estimatedMinutes = Math.ceil((totalFinalIdeas * (isQualityMode ? 60 : 30)) / 60);

  // Selected ideas for grid display
  const selectedIdeas = useMemo(() => {
    return results.filter((idea) => idea.isSelected);
  }, [results]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleTagToggle = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  const handleLearningGoalToggle = (goalId: number) => {
    setSelectedLearningGoalIds((prev) =>
      prev.includes(goalId) ? prev.filter((id) => id !== goalId) : [...prev, goalId]
    );
  };

  const handleSkillLevelToggle = (level: number) => {
    setSelectedSkillLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
    );
  };

  const handleDurationToggle = (duration: string) => {
    setSelectedDurations((prev) =>
      prev.includes(duration) ? prev.filter((d) => d !== duration) : [...prev, duration]
    );
  };

  const handleModifierToggle = (key: keyof typeof modifiers) => {
    setModifiers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleGenerate = async () => {
    if (selectedTagIds.length === 0 && selectedLearningGoalIds.length === 0) {
      toast.error('Please select at least one tag or learning goal');
      return;
    }
    if (selectedSkillLevels.length === 0) {
      toast.error('Please select at least one skill level');
      return;
    }

    const request: UnifiedIdeaGenerationRequest = {
      tags: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      learningGoals: selectedLearningGoalIds.length > 0 ? selectedLearningGoalIds : undefined,
      skillLevels: selectedSkillLevels,
      ideasPerCombination,
      candidatesPerIdea,
      // Pass durations array; empty array means "any" (no constraint)
      durations: selectedDurations.length > 0 ? selectedDurations : undefined,
      seedText: seedText || undefined,
      modifiers: Object.values(modifiers).some(Boolean) ? modifiers : undefined,
    };

    try {
      const response = await generateMutation.mutateAsync(request);
      setResults(response.ideas);

      toast.success(
        `Generated ${response.metadata.totalSelected} ideas from ${response.metadata.totalCandidates} candidates ` +
          `in ${response.metadata.generationTimeSeconds.toFixed(0)}s`
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate ideas');
    }
  };

  const handleUseIdea = (idea: UnifiedIdeaCandidate) => {
    // Build URL with pre-filled parameters for Episode Generator
    const params = new URLSearchParams();
    params.set('idea', idea.idea);
    params.set('complexity', String(idea.skillLevel));

    // Map duration name to duration ID
    const durationMap: Record<string, string> = {
      Micro: '1',
      Short: '2',
      Medium: '3',
      Long: '4',
      Extended: '5',
    };
    if (idea.duration in durationMap) {
      params.set('duration_id', durationMap[idea.duration]);
    }

    // Add tag IDs (convert to string for URL params)
    idea.tagIds.forEach((tagId) => {
      params.append('tags', String(tagId));
    });

    // Open in new tab
    window.open(`/episodes/generate?${params.toString()}`, '_blank');
  };

  const handleCopyIdea = (idea: UnifiedIdeaCandidate) => {
    const text = [
      `Idea: ${idea.idea}`,
      `Tags: ${idea.tagNames.join(', ')}`,
      `Skill Level: ${idea.skillLevelName}`,
      `Duration: ${idea.duration}`,
    ].join('\n');

    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleSendToBulkGenerate = () => {
    if (selectedIdeas.length === 0) {
      toast.error('No selected ideas to send');
      return;
    }

    const ideas: ParsedIdea[] = selectedIdeas.map((idea, i) => ({
      index: i,
      idea: idea.idea,
      skillLevel: idea.skillLevel,
      duration: idea.duration || undefined,
      tagIds: idea.tagIds.length > 0 ? idea.tagIds : undefined,
      tagNames: idea.tagNames.length > 0 ? idea.tagNames.join(', ') : undefined,
      learningGoalId: idea.learningGoalId ?? undefined,
      learningGoalName: idea.learningGoalName ?? undefined,
    }));

    navigate('/episodes/bulk-generate', { state: { ideas } });
  };

  const handleExportFullCSV = () => {
    if (results.length === 0) {
      toast.error('No ideas to export');
      return;
    }

    const csv = generateCSV(results, 'en');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    downloadCSV(csv, `episode_ideas_full_${timestamp}.csv`);
    toast.success(`Exported ${results.length} candidates to CSV`);
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="container mx-auto px-4 py-8">
      <EpisodeNavigation />

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Episode Idea Generator</h1>
        <p className="mt-2 text-sm text-gray-600">
          AI-powered tool for generating creative episode ideas with quality selection
        </p>
      </div>

      {/* Generation Form */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Generation Settings</h2>

        {/* Tags & Learning Goals */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags ({selectedTagIds.length} selected)
            {selectedLearningGoalIds.length > 0 && (
              <span className="ml-2 text-purple-600">
                + {selectedLearningGoalIds.length} learning{' '}
                {selectedLearningGoalIds.length === 1 ? 'goal' : 'goals'}
              </span>
            )}
          </label>
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
            {Object.entries(tagsByLearningGoal).map(([goalId, goal]) => (
              <div key={goalId} className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                    {goal.icon_emoji && <span className="mr-2">{goal.icon_emoji}</span>}
                    {goal.name}
                  </h4>
                  <label className="flex items-center space-x-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedLearningGoalIds.includes(goal.id)}
                      onChange={() => handleLearningGoalToggle(goal.id)}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-xs text-purple-600 font-medium">Any tag</span>
                  </label>
                </div>
                <details className="group">
                  <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700 select-none mb-1">
                    {(() => {
                      const count = goal.tags.filter((t) => selectedTagIds.includes(t.id)).length;
                      return count > 0
                        ? `${count} tag${count === 1 ? '' : 's'} selected — show tags`
                        : 'Show tags';
                    })()}
                  </summary>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
                    {goal.tags.map((tag) => (
                      <label
                        key={tag.id}
                        className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTagIds.includes(tag.id)}
                          onChange={() => handleTagToggle(tag.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{getTagEnglishName(tag)}</span>
                      </label>
                    ))}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Skill Levels */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Skill Levels</label>
          <div className="flex flex-wrap gap-3">
            {skillLevels.map((level) => (
              <label key={level.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedSkillLevels.includes(level.id)}
                  onChange={() => handleSkillLevelToggle(level.id)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">{level.cefr_code}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Ideas per combo and Candidates per idea Row */}
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Ideas per Combination
            </label>
            <input
              type="number"
              min="1"
              max="5"
              value={ideasPerCombination}
              onChange={(e) => setIdeasPerCombination(Number(e.target.value))}
              className="w-20 px-2 py-1.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Advanced Options (collapsed by default) */}
        <details className="mb-6">
          <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 select-none">
            Advanced Options
            {(seedText ||
              selectedDurations.length > 0 ||
              candidatesPerIdea !== 1 ||
              Object.values(modifiers).some(Boolean)) && (
              <span className="ml-2 text-xs text-blue-600 font-normal">(customized)</span>
            )}
          </summary>
          <div className="mt-3 space-y-4 border-l-2 border-gray-200 ml-1 pl-4">
            {/* Candidates per Idea */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Candidates per Idea
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={candidatesPerIdea}
                onChange={(e) => setCandidatesPerIdea(Number(e.target.value))}
                className="w-20 px-2 py-1.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
              <span className="text-xs text-gray-500">
                {isQualityMode ? '(quality mode)' : '(fast mode)'}
              </span>
            </div>
            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (each selected duration adds a combination)
              </label>
              <div className="flex flex-wrap gap-3">
                {DURATION_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedDurations.includes(option.value)}
                      onChange={() => handleDurationToggle(option.value)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Seed Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seed Text (optional)
              </label>
              <textarea
                value={seedText}
                onChange={(e) => setSeedText(e.target.value)}
                placeholder="Optional context to guide idea generation..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Modifiers */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Modifiers</label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={modifiers.addCreativity}
                    onChange={() => handleModifierToggle('addCreativity')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">High creativity</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={modifiers.addCulturalDiversity}
                    onChange={() => handleModifierToggle('addCulturalDiversity')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Cultural diversity</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={modifiers.multiPartyConversations}
                    onChange={() => handleModifierToggle('multiPartyConversations')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Multi-party</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={modifiers.cultureSpecific}
                    onChange={() => handleModifierToggle('cultureSpecific')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Culture-specific</span>
                </label>
              </div>
            </div>
          </div>
        </details>

        {/* Generation Summary and Button */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            <strong>{totalCombinations}</strong> combinations ×{' '}
            <strong>{ideasPerCombination}</strong> ideas = <strong>{totalFinalIdeas}</strong> final
            ideas
            {isQualityMode && (
              <span className="text-gray-500"> ({totalCandidates} total candidates)</span>
            )}
            <br />
            <span className="text-gray-500">
              Estimated time: ~{estimatedMinutes} {estimatedMinutes === 1 ? 'minute' : 'minutes'}
            </span>
          </div>

          <button
            onClick={handleGenerate}
            disabled={
              isLoading ||
              (selectedTagIds.length === 0 && selectedLearningGoalIds.length === 0) ||
              selectedSkillLevels.length === 0
            }
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {isLoading ? 'Generating...' : 'Generate Ideas'}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Generating episode ideas...</p>
          <p className="text-sm text-gray-500 mt-1">
            This may take ~{estimatedMinutes} {estimatedMinutes === 1 ? 'minute' : 'minutes'}
          </p>
        </div>
      )}

      {/* Results Grid */}
      {!isLoading && selectedIdeas.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Generated Ideas</h2>
              <p className="text-sm text-gray-600">
                {selectedIdeas.length} {isQualityMode ? 'selected ' : ''}ideas
                {isQualityMode && ` (from ${results.length} candidates)`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSendToBulkGenerate}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
              >
                Send to Bulk Generate
              </button>
              <button
                type="button"
                onClick={handleExportFullCSV}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Export Full CSV
              </button>
            </div>
          </div>

          {/* Ideas Grid */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedIdeas.map((idea) => (
              <div
                key={`${idea.tagIds.join('-')}-${idea.skillLevel}-${idea.ideaNumber}-${idea.candidateNumber}`}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  {idea.learningGoalName ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      {idea.learningGoalName}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      {idea.tagNames.join(' + ')}
                    </span>
                  )}
                  <span className="text-xs text-gray-500">{idea.skillLevelName}</span>
                </div>

                {/* Idea Text */}
                <p className="text-sm text-gray-700 mb-3">{idea.idea}</p>

                {/* Duration */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500">{idea.duration}</span>
                  {idea.selectionReason && (
                    <span
                      className="text-xs text-green-600 truncate max-w-[60%]"
                      title={idea.selectionReason}
                    >
                      {idea.selectionReason}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleCopyIdea(idea)}
                    className="flex-1 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                  >
                    Copy
                  </button>
                  <button
                    onClick={() => handleUseIdea(idea)}
                    className="flex-1 px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
                  >
                    Use
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && results.length === 0 && (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          <p className="text-gray-600 font-medium mb-1">No ideas generated yet</p>
          <p className="text-sm text-gray-500">
            Select tags or learning goals and skill levels, then click "Generate Ideas" to start
          </p>
        </div>
      )}
    </div>
  );
}
